import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarBlank, Download, ArrowLeft, ArrowRight, Users, Clock, CurrencyDollar, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;          // Mon=1 … Sun=7
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}
function toISO(d) { return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtHours(min) {
  const h = Math.floor(min / 60); const m = Math.round(min % 60);
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

export default function HrWeeklyReport() {
  const [weekStart, setWeekStart] = useState(toISO(mondayOf(new Date())));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/reports/weekly", { params: { week_start: weekStart } });
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando reporte");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const exportCSV = () => {
    if (!report) return;
    const headers = ["Empleado", "Área", "Horas pactadas", "Horas efectivas", "Faltantes", "Retardos (min)", "Días retardo", "Días presente", "Días ausente", "Pago hora", "Pago base", "Penalización", "Bono", "Pago final"];
    const rows = report.rows.map((r) => [r.employee_name, r.area, r.weekly_hours_target, r.effective_hours, r.missing_hours, r.late_minutes_total, r.days_late, r.days_present, r.days_absent, r.hourly_wage, r.base_pay, r.penalty, r.bonus, r.final_pay]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `nomina_${weekStart}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const days = useMemo(() => {
    const start = new Date(weekStart + "T12:00:00");
    return Array.from({ length: 7 }, (_, i) => toISO(addDays(start, i)));
  }, [weekStart]);

  const prevWeek = () => setWeekStart(toISO(addDays(new Date(weekStart + "T12:00:00"), -7)));
  const nextWeek = () => setWeekStart(toISO(addDays(new Date(weekStart + "T12:00:00"), +7)));

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">RH</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Reporte semanal</h1>
          <p className="text-zinc-500 mt-1 text-sm">Asistencia, horas efectivas, retardos y pago sugerido</p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" onClick={prevWeek} data-testid="week-prev"><ArrowLeft size={14}/></Button>
          <div className="px-3 py-1.5 border border-zinc-200 rounded inline-flex items-center gap-2 bg-white">
            <CalendarBlank size={14} className="text-zinc-500"/>
            <span className="text-sm font-mono-data">{weekStart} — {toISO(addDays(new Date(weekStart + "T12:00:00"), 6))}</span>
          </div>
          <Button variant="outline" size="sm" onClick={nextWeek} data-testid="week-next"><ArrowRight size={14}/></Button>
          <Button onClick={exportCSV} variant="outline" size="sm" className="gap-1" data-testid="export-csv"><Download size={14}/> CSV</Button>
        </div>
      </header>

      {/* Totals */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Empleados" value={report.totals.employees} color="#002FA7"/>
          <Kpi icon={Clock} label="Min. retardo total" value={report.totals.total_late_minutes} color="#FFC000"/>
          <Kpi icon={CurrencyDollar} label="Nómina total" value={`$${report.totals.grand_pay.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} color="#00873D"/>
          <Kpi icon={WarningCircle} label="Modo penalización" value={report.settings.penalty_mode} color="#7E57C2"/>
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">Cargando…</div>
        ) : !report || report.rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 dotted-bg">Sin empleados activos esta semana</div>
        ) : (
          <table className="w-full text-sm" data-testid="weekly-table">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-3 text-left">Empleado</th>
                <th className="px-3 py-3 text-right">Hrs pact.</th>
                <th className="px-3 py-3 text-right">Hrs efect.</th>
                <th className="px-3 py-3 text-right">Faltantes</th>
                <th className="px-3 py-3 text-right">Retardos</th>
                <th className="px-3 py-3 text-right">Días pres.</th>
                <th className="px-3 py-3 text-right">Pago base</th>
                <th className="px-3 py-3 text-right">Penal.</th>
                <th className="px-3 py-3 text-right">Final</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <>
                <tr key={r.employee_id} onClick={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`weekly-row-${r.employee_id}`}>
                  <td className="px-3 py-3 font-semibold">{r.employee_name}<br/><span className="text-[11px] text-zinc-400 font-normal">{r.area}</span></td>
                  <td className="px-3 py-3 text-right numeric">{r.weekly_hours_target}</td>
                  <td className="px-3 py-3 text-right numeric font-semibold">{r.effective_hours}</td>
                  <td className="px-3 py-3 text-right numeric" style={{ color: r.missing_hours > 0 ? "#FFC000" : "#52525B" }}>{r.missing_hours > 0 ? `-${r.missing_hours}` : "—"}</td>
                  <td className="px-3 py-3 text-right numeric" style={{ color: r.late_minutes_total > 0 ? "#E0000F" : "#52525B" }}>{r.late_minutes_total > 0 ? `${r.late_minutes_total} min` : "—"}</td>
                  <td className="px-3 py-3 text-right numeric">{r.days_present}/{r.days_present + r.days_absent}</td>
                  <td className="px-3 py-3 text-right numeric">${r.base_pay.toFixed(2)}</td>
                  <td className="px-3 py-3 text-right numeric" style={{ color: r.penalty > 0 ? "#E0000F" : "#52525B" }}>{r.penalty > 0 ? `-$${r.penalty.toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-3 text-right numeric font-bold text-[#00873D]">${r.final_pay.toFixed(2)}</td>
                </tr>
                {expanded === r.employee_id && (
                  <tr className="bg-zinc-50">
                    <td colSpan={9} className="px-3 py-3">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="text-zinc-500">
                            <tr>
                              <th className="text-left px-2 py-1">Día</th>
                              <th className="text-left px-2 py-1">Entrada</th>
                              <th className="text-left px-2 py-1">Comida →</th>
                              <th className="text-left px-2 py-1">← Regreso</th>
                              <th className="text-left px-2 py-1">Salida</th>
                              <th className="text-right px-2 py-1">Bruto</th>
                              <th className="text-right px-2 py-1">Efect.</th>
                              <th className="text-right px-2 py-1">Retardo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {days.map((dateStr) => {
                              const day = r.days.find((dd) => dd.date === dateStr);
                              const dn = new Date(dateStr + "T12:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });
                              if (!day) return <tr key={dateStr} className="border-t border-zinc-200"><td className="px-2 py-1 text-zinc-500" colSpan={8}>{dn} · Sin checadas</td></tr>;
                              const isAbsent = day.status === "absent";
                              return (
                                <tr key={dateStr} className="border-t border-zinc-200" style={{ background: isAbsent ? "rgba(224,0,15,.05)" : "transparent" }}>
                                  <td className="px-2 py-1 capitalize">{dn}{isAbsent && <span className="ml-1 text-red-600 text-[10px] font-semibold">(falta)</span>}</td>
                                  <td className="px-2 py-1 numeric">{fmtTime(day.entry_actual)}</td>
                                  <td className="px-2 py-1 numeric">{fmtTime(day.lunch_out_actual)}</td>
                                  <td className="px-2 py-1 numeric">{fmtTime(day.lunch_in_actual)}</td>
                                  <td className="px-2 py-1 numeric">{fmtTime(day.exit_actual)}</td>
                                  <td className="px-2 py-1 text-right numeric">{fmtHours(day.gross_minutes)}</td>
                                  <td className="px-2 py-1 text-right numeric font-semibold">{fmtHours(day.effective_minutes)}</td>
                                  <td className="px-2 py-1 text-right numeric" style={{ color: day.late_minutes > 0 ? "#E0000F" : "#52525B" }}>{day.late_minutes > 0 ? `${day.late_minutes} min` : "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="p-4 border border-zinc-200 rounded bg-white">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-mono-data">
        <Icon size={14} style={{ color }}/> {label}
      </div>
      <div className="font-heading text-2xl font-extrabold mt-1 numeric" style={{ color }}>{value}</div>
    </div>
  );
}
